// IndexedDB persistence for the browser folder-sync feature: the picked
// FileSystemDirectoryHandles (can't go in localStorage) and the per-file
// change-detection map (lastModified/size), so a page reload doesn't make
// every file look "new" again.

export type FolderType = "gdt" | "dat";

export interface SyncFileState {
    lastModified: number;
    size: number;
    // "skipped" = staff declined the confirmation prompt for this exact version of the
    // file. Storing its lastModified/size like a normal processed entry silences it from
    // re-prompting every poll — it's treated as "unchanged" until the file is actually
    // modified again, at which point it naturally re-qualifies for a fresh prompt.
    status: "processed" | "failed" | "skipped";
    lastError?: string;
}

export interface SyncLogEntry {
    id: string;
    timestamp: number;
    folderType: FolderType;
    filename: string;
    // "cancelled" is its own status (not "skipped") so a staff-initiated undo of an already-
    // committed write reads as a deliberate, notable action rather than the quiet "nothing
    // happened" styling "skipped" gets — see cancelGdtReview in PraxisSoftwareSyncContext.tsx.
    status: "success" | "error" | "skipped" | "cancelled";
    message: string;
}

const DB_NAME = "medinvoice-gdt-sync";
const DB_VERSION = 3;
const HANDLES_STORE = "directoryHandles";
const FILE_STATE_STORE = "fileState";
const SYNC_LOG_STORE = "syncLog";
const SYNC_LOG_MAX_ENTRIES = 200;
const PREFERENCES_STORE = "preferences";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(HANDLES_STORE)) {
                db.createObjectStore(HANDLES_STORE);
            }
            if (!db.objectStoreNames.contains(FILE_STATE_STORE)) {
                db.createObjectStore(FILE_STATE_STORE);
            }
            if (!db.objectStoreNames.contains(SYNC_LOG_STORE)) {
                // autoIncrement key doubles as insertion order, so entries can be
                // read back newest-first without a separate timestamp index.
                db.createObjectStore(SYNC_LOG_STORE, { autoIncrement: true });
            }
            if (!db.objectStoreNames.contains(PREFERENCES_STORE)) {
                db.createObjectStore(PREFERENCES_STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    // Don't cache a failed open: otherwise one transient failure breaks every later call until a reload.
    dbPromise.catch(() => { dbPromise = null; });
    return dbPromise;
}

function runTx<T>(
    storeName: string,
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
    return openDb().then(
        (db) =>
            new Promise<T>((resolve, reject) => {
                const tx = db.transaction(storeName, mode);
                const store = tx.objectStore(storeName);
                const req = fn(store);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            }),
    );
}

export async function getHandle(type: FolderType): Promise<FileSystemDirectoryHandle | null> {
    const result = await runTx<FileSystemDirectoryHandle | undefined>(
        HANDLES_STORE,
        "readonly",
        (store) => store.get(type),
    );
    return result ?? null;
}

export async function setHandle(type: FolderType, handle: FileSystemDirectoryHandle): Promise<void> {
    await runTx(HANDLES_STORE, "readwrite", (store) => store.put(handle, type));
}

export async function clearHandle(type: FolderType): Promise<void> {
    await runTx(HANDLES_STORE, "readwrite", (store) => store.delete(type));
}

function fileStateKey(type: FolderType, filename: string): string {
    return `${type}:${filename}`;
}

export async function getFileState(type: FolderType): Promise<Record<string, SyncFileState>> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(FILE_STATE_STORE, "readonly");
        const store = tx.objectStore(FILE_STATE_STORE);
        const prefix = `${type}:`;
        const result: Record<string, SyncFileState> = {};
        const req = store.openCursor();
        req.onsuccess = () => {
            const cursor = req.result;
            if (!cursor) { resolve(result); return; }
            const key = cursor.key as string;
            if (key.startsWith(prefix)) {
                result[key.slice(prefix.length)] = cursor.value as SyncFileState;
            }
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

export async function setFileState(type: FolderType, filename: string, entry: SyncFileState): Promise<void> {
    await runTx(FILE_STATE_STORE, "readwrite", (store) => store.put(entry, fileStateKey(type, filename)));
}

/** Appends one sync-log entry and prunes the oldest entries beyond
 *  SYNC_LOG_MAX_ENTRIES in the same transaction, so the store never grows
 *  unbounded across a long-lived browser profile. */
export async function appendSyncLogEntry(entry: SyncLogEntry): Promise<void> {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(SYNC_LOG_STORE, "readwrite");
        const store = tx.objectStore(SYNC_LOG_STORE);
        store.add(entry);

        // Prune oldest-first once the store exceeds the cap.
        const countReq = store.count();
        countReq.onsuccess = () => {
            const overflow = countReq.result - SYNC_LOG_MAX_ENTRIES;
            if (overflow <= 0) return;
            let deleted = 0;
            const cursorReq = store.openCursor();
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (!cursor || deleted >= overflow) return;
                cursor.delete();
                deleted++;
                cursor.continue();
            };
        };

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

/** Reads persisted sync-log entries, newest first. */
export async function getSyncLog(limit = SYNC_LOG_MAX_ENTRIES): Promise<SyncLogEntry[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(SYNC_LOG_STORE, "readonly");
        const store = tx.objectStore(SYNC_LOG_STORE);
        const result: SyncLogEntry[] = [];
        const req = store.openCursor(null, "prev");
        req.onsuccess = () => {
            const cursor = req.result;
            if (!cursor || result.length >= limit) { resolve(result); return; }
            result.push(cursor.value as SyncLogEntry);
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

/** Deletes every persisted sync-log entry. */
export async function clearSyncLog(): Promise<void> {
    await runTx(SYNC_LOG_STORE, "readwrite", (store) => store.clear());
}

// Auto-processing used to live here as a per-browser preference; it's now a praxis-wide setting
// (Settings → Praxis Software Sync → getPraxisSoftwareSyncSettings, backend Settings.praxisSoftwareSync).
// PREFERENCES_STORE itself stays (removing an IndexedDB object store needs a version bump + migration
// for existing users, disproportionate for a store with nothing else in it).
